import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  CalendarDays,
  ClipboardList,
  DollarSign,
  Flag,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  LogOut,
  PlayCircle,
  Shield,
  Stethoscope,
  Trophy,
  Users,
} from 'lucide-react';
import { useTechAuth } from '@/contexts/TechAuthContext';
import { getPortalContext } from '@/lib/portal';
import { getTechBranding } from '@/lib/tech-branding';

function NavItem({
  to,
  label,
  icon,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          'flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors',
          isActive ? 'bg-brand-primary text-white' : 'text-gray-700 hover:bg-gray-100',
        ].join(' ')
      }
    >
      {icon}
      <span className="text-base">{label}</span>
    </NavLink>
  );
}

export default function TechLayout() {
  const {
    technician,
    signOut,
    isOnline,
    pendingEnrollmentCount,
    pendingEnrollmentSyncing,
    pendingEnrollmentError,
    syncPendingEnrollments,
  } = useTechAuth();
  const navigate = useNavigate();

  const portal = getPortalContext();
  const branding = getTechBranding(portal.companyId);

  const isManager = String(technician?.role || '').toLowerCase() === 'manager';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
              <img src={branding.logoPath} alt={`${branding.displayName} logo`} className="h-8 w-auto object-contain" />
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900">{branding.displayName} Technician Portal</div>
              <div className="text-sm text-gray-600">{technician?.full_name || 'Signed in'}</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {!isOnline && (
              <div className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
                Offline mode
              </div>
            )}

            {pendingEnrollmentCount > 0 && (
              <button
                type="button"
                onClick={() => syncPendingEnrollments()}
                disabled={!isOnline || pendingEnrollmentSyncing}
                className="px-3 py-1 rounded-full text-xs font-semibold border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                title={
                  isOnline
                    ? pendingEnrollmentSyncing
                      ? 'Syncing pending enrollments…'
                      : 'Sync pending enrollments'
                    : 'Go online to sync pending enrollments'
                }
              >
                Pending Sync: {pendingEnrollmentCount}
                {pendingEnrollmentSyncing ? '…' : ''}
              </button>
            )}

            <button
              type="button"
              onClick={handleSignOut}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      {pendingEnrollmentError && (
        <div className="bg-red-50 border-b border-red-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 text-sm text-red-800">
            Pending enrollment sync failed: {pendingEnrollmentError}
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <nav className="w-full lg:w-64 bg-white border border-gray-200 rounded-2xl p-4">
            <div className="space-y-2">
              <NavItem to="/dashboard" label="Dashboard" icon={<LayoutDashboard className="w-5 h-5" />} />
              <NavItem to="/schedule" label="Schedule" icon={<CalendarDays className="w-5 h-5" />} />
              <NavItem to="/leaderboard" label="Leaderboard" icon={<Trophy className="w-5 h-5" />} />
              <NavItem to="/presentation" label="Presentation Mode" icon={<PlayCircle className="w-5 h-5" />} />
              <NavItem to="/diagnostics" label="Diagnostics" icon={<Stethoscope className="w-5 h-5" />} />
              <NavItem to="/enroll" label="Enroll Customer" icon={<ClipboardList className="w-5 h-5" />} />
              <NavItem to="/past-calls" label="Past Calls" icon={<ListChecks className="w-5 h-5" />} />
              <NavItem to="/spiffs" label="Spiffs" icon={<DollarSign className="w-5 h-5" />} />
              <NavItem to="/portfolio" label="Customer Portfolio" icon={<Users className="w-5 h-5" />} />
              <NavItem to="/account" label="Account" icon={<KeyRound className="w-5 h-5" />} />
              {isManager && (
                <>
                  <NavItem to="/admin/performance" label="Performance" icon={<BarChart3 className="w-5 h-5" />} />
                  <NavItem to="/admin/contests" label="Contests" icon={<Flag className="w-5 h-5" />} />
                  <NavItem to="/admin" label="Admin" icon={<Shield className="w-5 h-5" />} />
                </>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="text-xs text-gray-500">Spiffs: $10 new, $10 renewal, $5 upgrade</div>
              <div className="text-xs text-gray-500 mt-1">(Earned totals visible on dashboard)</div>
            </div>
          </nav>

          <section className="flex-1">
            <Outlet />
          </section>
        </div>
      </main>
    </div>
  );
}
